import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  applyBulletMaskToInput,
  derivePasswordFromMaskedInput,
  HP_PW_BULLET,
} from '../masked-bullet-input.util';

@Component({
  selector: 'app-healthpicks-operator-password-input',
  templateUrl: './healthpicks-operator-password-input.component.html',
  styleUrls: ['./healthpicks-operator-password-input.component.css', '../style/global.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => HealthpicksOperatorPasswordInputComponent),
    },
  ],
})
export class HealthpicksOperatorPasswordInputComponent
  implements ControlValueAccessor, AfterViewInit
{
  @Input()
  placeholder = '';
  @Input()
  name = '';

  inputState = {
    isShow: false,
    isFocus: false,
  };

  @ViewChild('innerPassword', { static: false })
  innerPassword?: ElementRef<HTMLInputElement>;

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  private _password = '';

  get password(): string {
    return this._password;
  }

  writeValue(obj: any): void {
    this._password = obj == null ? '' : String(obj);
    queueMicrotask(() => this.syncInnerInputFromModel());
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  ngAfterViewInit(): void {
    this.syncInnerInputFromModel();
  }

  /** Figma-style ● mask (not browser password bullets). */
  get maskBulletText(): string {
    if (this.inputState.isShow) {
      return '';
    }
    const len = this._password?.length ?? 0;
    return len > 0 ? HP_PW_BULLET.repeat(len) : '';
  }

  onInnerInput(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    if (this.inputState.isShow) {
      this._password = el.value;
      this.onChangeCallback(this._password);
      return;
    }
    const next = derivePasswordFromMaskedInput(el.value, this._password);
    this._password = next;
    this.onChangeCallback(next);
    applyBulletMaskToInput(el, next);
  }

  onToggleReveal(): void {
    this.inputState.isShow = !this.inputState.isShow;
    queueMicrotask(() => this.syncInnerInputFromModel());
  }

  syncInnerInputFromModel(): void {
    const el = this.innerPassword?.nativeElement;
    if (!el) {
      return;
    }
    if (this.inputState.isShow) {
      if (el.value !== this._password) {
        el.value = this._password;
        const end = this._password.length;
        requestAnimationFrame(() => {
          try {
            el.setSelectionRange(end, end);
          } catch {
            /* ignore */
          }
        });
      }
      return;
    }
    applyBulletMaskToInput(el, this._password);
  }

  blur() {
    this.inputState.isFocus = false;
    queueMicrotask(() => this.syncInnerInputFromModel());
  }

  focus() {
    this.inputState.isFocus = true;
    queueMicrotask(() => this.syncInnerInputFromModel());
  }

  touched(): void {
    this.onTouchedCallback();
  }
}
